import { useState } from 'react';
import { motion } from 'framer-motion';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Heart, X, CheckCircle2, Loader2, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const PRESETS = [3, 5, 10, 20];

/**
 * TipJarModal — donation flow for an individual artist via PayPal.
 *
 * props:
 *   - artist: { id, name, image }
 *   - onClose
 */
export default function TipJarModal({ artist, onClose }) {
  const [amount, setAmount] = useState(5);
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | processing | success | error
  const [error, setError] = useState('');

  const finalAmount = customAmount ? parseFloat(customAmount) : amount;
  const valid = finalAmount >= 1 && finalAmount <= 500;

  const createOrder = async () => {
    setError('');
    if (!valid) {
      setError('Montant invalide (1 a 500 EUR)');
      throw new Error('invalid amount');
    }
    const r = await fetch(`${API}/paypal/create-tip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        artist_id: artist.id,
        artist_name: artist.name,
        amount: finalAmount,
        currency: 'EUR',
        message: message || undefined,
        supporter_email: email || undefined,
      }),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      setError(d.detail || `Erreur ${r.status}`);
      throw new Error(d.detail || 'paypal create-tip failed');
    }
    const d = await r.json();
    return d.id;
  };

  const onApprove = async (data) => {
    setStatus('processing');
    try {
      const r = await fetch(`${API}/paypal/capture-order/${data.orderID}`, { method: 'POST' });
      if (!r.ok) throw new Error('capture failed');
      setStatus('success');
    } catch (e) {
      setError('Le paiement a abouti mais nous n\'avons pas pu confirmer. Verifie tes emails PayPal.');
      setStatus('error');
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[140] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="tip-jar-modal">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-zinc-950 border border-pink-500/30 rounded-2xl w-full max-w-lg shadow-2xl my-8">
        {/* Header */}
        <div className="relative p-5 border-b border-zinc-800 bg-gradient-to-br from-pink-500/15 via-orange-500/10 to-transparent rounded-t-2xl">
          <button onClick={onClose} className="absolute top-3 right-3 text-zinc-500 hover:text-white p-2"><X size={18} /></button>
          <div className="flex items-center gap-3">
            {artist.image && (
              <img src={artist.image} alt={artist.name}
                className="w-14 h-14 rounded-xl object-cover border-2 border-pink-500/40" />
            )}
            <div>
              <p className="text-[10px] text-pink-400 font-bold uppercase tracking-widest flex items-center gap-1.5">
                <Heart size={11} fill="currentColor" /> Soutenir l'artiste
              </p>
              <h3 className="text-xl font-black text-white">{artist.name}</h3>
              <p className="text-[11px] text-zinc-400">Tip 100% reverse a l'artiste via PayPal</p>
            </div>
          </div>
        </div>

        {status === 'success' ? (
          <div className="p-8 text-center">
            <CheckCircle2 size={56} className="text-green-400 mx-auto mb-3" />
            <h4 className="text-xl font-black text-white mb-2">Merci infiniment !</h4>
            <p className="text-sm text-zinc-400 mb-1">Ton soutien de <span className="text-pink-400 font-bold">{finalAmount.toFixed(2)} EUR</span> est arrive.</p>
            <p className="text-xs text-zinc-500 mb-4">{artist.name} et toute l'equipe KAZAJAH te remercient.</p>
            <button onClick={onClose} className="px-6 py-2 bg-pink-500 hover:bg-pink-400 text-white font-bold rounded-lg text-sm">Fermer</button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {/* Amount presets */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-2">Choisis un montant</p>
              <div className="grid grid-cols-4 gap-2 mb-2">
                {PRESETS.map(p => (
                  <button key={p} onClick={() => { setAmount(p); setCustomAmount(''); }}
                    data-testid={`tip-preset-${p}`}
                    className={`py-3 rounded-xl font-black text-base transition-all ${
                      !customAmount && amount === p
                        ? 'bg-gradient-to-br from-pink-500 to-orange-500 text-white shadow-lg shadow-pink-500/40 scale-105'
                        : 'bg-zinc-900 border border-zinc-800 text-zinc-300 hover:border-pink-500/40'
                    }`}>
                    {p} €
                  </button>
                ))}
              </div>
              <div className="relative">
                <input type="number" min="1" max="500" step="0.5"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="Autre montant (1-500)"
                  data-testid="tip-custom-amount"
                  className="w-full px-4 py-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-white text-sm pr-10 focus:outline-none focus:border-pink-500" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">EUR</span>
              </div>
            </div>

            {/* Optional message */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Message (optionnel)</p>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder={`Un mot pour ${artist.name} ?`}
                rows={2} maxLength={280}
                data-testid="tip-message"
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-xl text-white text-sm focus:outline-none focus:border-pink-500 resize-none" />
            </div>

            {/* Optional email */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Email (optionnel — pour recevoir un merci)</p>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="ton@email.com"
                data-testid="tip-email"
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-xl text-white text-sm focus:outline-none focus:border-pink-500" />
            </div>

            {error && (
              <div className="p-2.5 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-300">{error}</div>
            )}

            {/* PayPal */}
            <div className="pt-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-2 flex items-center justify-between">
                <span>Total a payer</span>
                <span className="text-2xl text-pink-400 font-black normal-case tracking-normal">{finalAmount.toFixed(2)} EUR</span>
              </p>
              {status === 'processing' ? (
                <div className="text-center py-6">
                  <Loader2 size={28} className="animate-spin text-pink-400 mx-auto mb-2" />
                  <p className="text-sm text-zinc-300">Confirmation du paiement…</p>
                </div>
              ) : valid && PAYPAL_CLIENT_ID ? (
                <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: 'EUR', intent: 'capture' }}>
                  <PayPalButtons
                    style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay', height: 42 }}
                    createOrder={createOrder}
                    onApprove={onApprove}
                    onError={() => { setError('Erreur PayPal. Reessaie.'); setStatus('error'); }}
                    onCancel={() => setStatus('idle')}
                    forceReRender={[finalAmount, message, email]}
                  />
                </PayPalScriptProvider>
              ) : (
                <div className="text-center text-xs text-zinc-500 py-4">
                  Renseigne un montant entre 1 et 500 EUR pour activer le paiement.
                </div>
              )}
              <p className="text-[10px] text-zinc-500 text-center mt-2 flex items-center justify-center gap-1">
                <Sparkles size={9} /> Paiement securise via PayPal — frais ~2.9 % + 0.35 EUR
              </p>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
